import { Hub } from "aws-amplify/utils";
import { navigate, withPrefix } from "gatsby";
import { useContext, useEffect } from "react";
import { IUseAuthModalContext } from "../context/AuthModalContext";
import useModalStore, { setIsClosable } from "../store/useModalStore";
import { AuthListenerProps } from "../types";
import useAuth from "./useAuth";
import useEventDetails from "./useEventDetails";

export const useAuthModal = ({
  authRequired,
  redirectOnSignIn = "/virtual/live",
  redirectOnSignOut,
}: IUseAuthModalContext = {}) => {
  const { user, loading, loggedOut, signOut } = useAuth();
  const { opened, toggleModal, openModal, closeModal } = useModalStore();
  const { isDuringEvent } = useEventDetails();
  // Listen to Amplify HUB auth channel to close/open the modal
  const authChannelListener: unknown = ({
    channel,
    payload: { event, message },
  }: AuthListenerProps) => {
    if (channel !== "auth") {
      return;
    }
    switch (event) {
      case "signIn":
        closeModal();
        isDuringEvent && redirectOnSignIn && navigate(redirectOnSignIn, { replace: true });
        break;
      case "signOut":
        redirectOnSignOut &&
          navigate(withPrefix(redirectOnSignOut), { replace: true });
        authRequired && openModal();
        break;
      default:
        console.warn(
          `hub.event ${event} dispatched in ${channel} channel. ${message}`
        );
        break;
    }
  };

  useEffect(() => {
    // @ts-ignore
    const listenerCancel = Hub.listen("auth", authChannelListener);
    // @ts-ignore
    return () => listenerCancel();
  }, []);

  useEffect(() => {
    if (!authRequired) {
      return;
    }

    if (authRequired && Boolean(user)) {
      closeModal();
    } else {
      setIsClosable(false);
      openModal();
    }
    return () => {
      setIsClosable(true);
    };
  }, [authRequired, user]);

  return {
    opened,
    user,
    loading,
    loggedOut,
    toggleModal,
    openModal,
    closeModal,
    signOut,
  };
};

export default useAuthModal;
